import { copy } from '@ohear/shared'
import { Badge, Button, Textarea, useToast } from '@ohear/ui'
/**
 * Single-take review pane.
 *
 * Used both as the inline expansion of a queue row and as the body of the
 * full review Modal. Includes:
 *   - Full HTML5 audio player for the take.
 *   - Reference voice A/B player (if the cue has a reference).
 *   - QC metrics breakdown table.
 *   - Supervisor note input.
 *   - Decision buttons (approve / pickup / archive).
 */
import { useId, useRef, useState } from 'react'
import { useDecideTake } from '../../lib/api/supervisor'
import type { PendingTake } from '../../lib/api/supervisor'
import { useT } from '../../lib/i18n/use-t'

export interface TakeReviewerProps {
  projectId: string
  take: PendingTake
  /** Called after any successful decision. */
  onDecided?: () => void
  /** Called when "Request pickup" is pressed; opens a separate Modal. */
  onRequestPickup?: (take: PendingTake) => void
  /** Hide the pickup CTA (useful when reviewer is already inside the pickup modal). */
  hidePickupButton?: boolean
}

export function TakeReviewer({
  projectId,
  take,
  onDecided,
  onRequestPickup,
  hidePickupButton,
}: TakeReviewerProps) {
  const t = useT()
  const decide = useDecideTake(projectId)
  const toast = useToast()
  const noteId = useId()
  const [note, setNote] = useState('')
  const takeRef = useRef<HTMLAudioElement | null>(null)
  const refRef = useRef<HTMLAudioElement | null>(null)

  const playPair = () => {
    // A/B: play reference, then the take. Browsers serialize via `ended`.
    const a = refRef.current
    const b = takeRef.current
    if (!a || !b) return
    a.currentTime = 0
    b.pause()
    b.currentTime = 0
    void a.play()
    a.onended = () => {
      void b.play()
    }
  }

  const decideWith = (decision: 'approved' | 'archived') => {
    decide.mutate(
      { takeId: take.take_id, decision, supervisor_note: note.trim() || null },
      {
        onSuccess: () => {
          toast.show({
            severity: decision === 'approved' ? 'success' : 'info',
            title: decision === 'approved' ? 'Take approved' : 'Take archived',
            description: `${take.character_display_name} cue ${take.cue_number}.`,
          })
          onDecided?.()
        },
        onError: (err) => {
          toast.show({
            severity: 'error',
            title: 'Decision failed',
            description: err instanceof Error ? err.message : 'Unknown error',
          })
        },
      },
    )
  }

  const failCount = take.qc.filter((q) => q.severity === 'fail').length
  const warnCount = take.qc.filter((q) => q.severity === 'warn').length

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-center gap-2">
        <Badge variant="gray">{take.character_code}</Badge>
        <span className="font-medium">{take.character_display_name}</span>
        <span className="text-[var(--text-2)]">
          cue {take.cue_number} - v{take.version}
        </span>
        {take.actor_display_name ? (
          <span className="text-[var(--text-2)]">- {take.actor_display_name}</span>
        ) : null}
        <span className="ml-auto text-[12px] text-[var(--text-2)] tabular-nums">
          {take.duration_sec.toFixed(2)}s
        </span>
      </header>

      <p className="m-0 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--bg-3)] p-3 text-[14px]">
        {take.cue_text}
      </p>

      <section
        className="grid grid-cols-1 md:grid-cols-2 gap-3"
        aria-label="Audio"
        data-tour="ab-compare"
      >
        <div>
          <h4 className="text-[12px] font-medium text-[var(--text-2)] m-0 mb-1">Take</h4>
          <audio
            ref={takeRef}
            src={take.audio_url}
            controls
            preload="metadata"
            className="w-full"
          />
        </div>
        <div>
          <h4 className="text-[12px] font-medium text-[var(--text-2)] m-0 mb-1">Reference</h4>
          {take.reference ? (
            <audio
              ref={refRef}
              src={take.reference.url}
              controls
              preload="metadata"
              className="w-full"
            />
          ) : (
            <div className="text-[13px] text-[var(--text-3)] py-2">
              {t(copy.common.EMPTY.no_reference_for_cue)}
            </div>
          )}
        </div>
      </section>

      {take.reference ? (
        <div>
          <Button size="sm" variant="ghost" onClick={playPair}>
            Play A/B (reference - take)
          </Button>
        </div>
      ) : null}

      <section aria-label="QC">
        <div className="flex items-center gap-2 mb-2">
          <h4 className="text-[13px] font-medium m-0">QC</h4>
          {failCount > 0 ? <Badge qcSeverity="fail">{failCount} fail</Badge> : null}
          {warnCount > 0 ? <Badge qcSeverity="warn">{warnCount} warn</Badge> : null}
          {failCount === 0 && warnCount === 0 ? <Badge qcSeverity="pass">all pass</Badge> : null}
        </div>
        {take.qc.length === 0 ? (
          <p className="text-[13px] text-[var(--text-2)] m-0">No QC checks reported.</p>
        ) : (
          <ul className="m-0 p-0 list-none flex flex-col gap-1">
            {take.qc.map((q) => (
              <li
                key={q.check_id}
                className="flex items-center gap-2 text-[13px] border-b border-[var(--border)] py-1 last:border-b-0"
              >
                <Badge qcSeverity={q.severity}>{q.check_id}</Badge>
                <span className="text-[var(--text-2)]">{q.friendly_message ?? '-'}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div>
        <label htmlFor={noteId} className="block text-[13px] font-medium text-[var(--text)] mb-1">
          Supervisor note (optional)
        </label>
        <Textarea
          id={noteId}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder="Notes attached to this decision; visible to the actor on pickups."
          disabled={decide.isPending}
        />
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button variant="ghost" onClick={() => decideWith('archived')} disabled={decide.isPending}>
          Archive
        </Button>
        {!hidePickupButton ? (
          <Button
            variant="danger"
            onClick={() => onRequestPickup?.(take)}
            disabled={decide.isPending}
          >
            Request pickup
          </Button>
        ) : null}
        <Button variant="primary" onClick={() => decideWith('approved')} loading={decide.isPending}>
          Approve
        </Button>
      </div>
    </div>
  )
}
